import { Notification, Title } from 'rbx';
import React, { useCallback, useEffect, useState } from 'react';
import { css } from 'emotion';
import {
  cancelAutoApplySubscription,
  cancelDomainSubscription,
  getAutoApplyCredits,
  getAutoApplyHistory,
  getAutoApplySubscription,
  getDomainSubscriptionHistory,
  listDomainSubscriptions,
  refundAutoApplyOrder,
  refundDomainOrder,
} from '../../lib/api';
import { Loading } from '../../components';
import useUser from '../../hooks/useUser';
import DomainSubscriptions from './components/DomainSubscriptions';
import AutoApplySubscription from './components/AutoApplySubscription';

const EMPTY = {
  domains: [],
  domainHistory: { orders: [], invoices: [] },
  autoApply: null,
  credits: null,
  autoApplyHistory: { orders: [], invoices: [] },
};

/**
 * The user's add-on subscriptions — .cv domains and auto-apply (Skyvern) —
 * with their charges and invoices. Shown on the account page under the main
 * subscription, and scoped to that user the same way.
 */
function AddOnsPage() {
  const { user } = useUser();
  const [data, setData] = useState(EMPTY);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async userId => {
    setLoading(true);
    // Each part loads on its own: one failing request (an endpoint the
    // billing it points at does not have yet, say) must not hide the rest.
    const parts = {
      domains: listDomainSubscriptions,
      domainHistory: getDomainSubscriptionHistory,
      autoApply: getAutoApplySubscription,
      credits: getAutoApplyCredits,
      autoApplyHistory: getAutoApplyHistory,
    };
    const names = Object.keys(parts);
    const results = await Promise.allSettled(
      names.map(name => parts[name](userId)),
    );

    const next = { ...EMPTY };
    const failed = [];
    results.forEach((result, i) => {
      const name = names[i];
      if (result.status === 'fulfilled') {
        if (result.value) next[name] = result.value;
      } else {
        failed.push(`${name}: ${result.reason && result.reason.message}`);
      }
    });
    setData(next);
    setError(failed.length ? `Could not load ${failed.join('; ')}` : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      load(user.id);
    } else {
      setData(EMPTY);
    }
  }, [user, load]);

  // Every action reloads everything, so the page shows billing's result.
  const act = fn => async (...args) => {
    await fn(user.id, ...args);
    await load(user.id);
  };

  if (!user) return null;

  return (
    <div
      className={css`
        padding: 1em 0em;
      `}
    >
      <header
        className={css`
          padding: 0.2em 0em;
        `}
      >
        <Title size={3}>Add-ons</Title>
      </header>
      {error && <Notification color="danger">{error}</Notification>}
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <DomainSubscriptions
            subscriptions={data.domains}
            history={data.domainHistory}
            onCancel={act(cancelDomainSubscription)}
            onRefund={act(refundDomainOrder)}
          />
          <AutoApplySubscription
            subscription={data.autoApply}
            credits={data.credits}
            history={data.autoApplyHistory}
            onCancel={act(cancelAutoApplySubscription)}
            onRefund={act(refundAutoApplyOrder)}
          />
        </>
      )}
    </div>
  );
}

export default AddOnsPage;
